import { Title } from "lola-framework-ui-test";
import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MailStrokeIcon, WhatsAppStrokeIcon } from "../assets/icons";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { RequestStatus } from "../utils/commons";
import { TITLE_COLOR } from "../constants";
import { Popup } from "./Popup";
import { CloseLight } from "../assets/navIcons";

interface SupportChannels {
  email?: string;
  whatsapp?: string;
}

interface SupportSheetProps {
  namespace: string;
  visible: boolean;
  onClose: () => void;
  /**
   * When set, these channels replace the mock session. Both missing means
   * the list is already known to be empty.
   */
  channels?: SupportChannels;
}

export const SupportSheet = ({
  namespace,
  visible,
  onClose,
  channels,
}: SupportSheetProps) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const email = channels
    ? channels.email
    : session.data?.partner?.supportEmail;
  const whatsapp = channels
    ? channels.whatsapp
    : session.data?.partner?.supportWhatsappNumber;
  const hasChannels = Boolean(email || whatsapp);
  const showSkeleton =
    !channels && session.status === RequestStatus.LOADING && !hasChannels;

  return (
    <Popup visible={visible} onClose={onClose}>
      <section
        className="support-sheet"
        aria-busy={showSkeleton}
        aria-label={t("contact.chooseOption", { ns: namespace })}
      >
        <header className="payout-details-header">
          <span className="payout-header-action" aria-hidden="true" />
          <Title
            title={t("contact.chooseOption", { ns: namespace })}
            align="center"
            color={bodyCopy}
          />
          <button
            type="button"
            className="payout-header-action payout-close-btn"
            aria-label={t("common.cancel", {
              ns: namespace,
              defaultValue: t("common.back", { ns: namespace }),
            })}
            onClick={onClose}
          >
            <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
          </button>
        </header>
        {showSkeleton ? (
          <div aria-hidden="true">
            {[0, 1, 2].map((index) => (
              <div key={index} className="payout-skeleton">
                <span className="payout-skeleton-icon" />
                <span className="payout-skeleton-lines">
                  <span className="payout-skeleton-line" />
                  <span className="payout-skeleton-line short" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <>
            {whatsapp && (
              <button type="button" className="support-sheet-option">
                <span
                  className="support-sheet-option-icon"
                  style={{ background: backgroundBtn }}
                >
                  <WhatsAppStrokeIcon
                    size={22}
                    colors={[textColorBtn, textColorBtn]}
                  />
                </span>
                <span style={{ color: TITLE_COLOR }}>
                  {t("contact.whatsapp", { ns: namespace })}
                </span>
              </button>
            )}
            {email && (
              <button type="button" className="support-sheet-option">
                <span
                  className="support-sheet-option-icon"
                  style={{ background: backgroundBtn }}
                >
                  <MailStrokeIcon
                    size={22}
                    colors={[textColorBtn, textColorBtn]}
                  />
                </span>
                <span style={{ color: TITLE_COLOR }}>
                  {t("contact.email", { ns: namespace })}
                </span>
              </button>
            )}
            {!hasChannels && (
              <p className="footer-text" style={{ color: bodyCopy }}>
                {t("contact.empty", {
                  ns: namespace,
                  defaultValue: t("contact.empty", { ns: "cash-deposit" }),
                })}
              </p>
            )}
          </>
        )}
      </section>
    </Popup>
  );
};

export const useSupportSheet = (namespace: string): {
  openSupport: () => void;
  supportSheet: ReactNode;
} => {
  const [visible, setVisible] = useState(false);
  const openSupport = useCallback(() => setVisible(true), []);
  const closeSupport = useCallback(() => setVisible(false), []);

  return {
    openSupport,
    supportSheet: (
      <SupportSheet
        namespace={namespace}
        visible={visible}
        onClose={closeSupport}
      />
    ),
  };
};
